import { useEffect, useMemo, useState } from "react";
import { Tag } from "primereact/tag";
import C from "classnames";

import { useUserChores } from "sj-ui-toolkit/src/api/hooks";
import { getPersonalizedLessonAssignment } from "sj-ui-toolkit/src/helpers";
import type { LessonChoreItem } from "sj-ui-toolkit/src/helpers/plaHelpers";
import { getContentClient } from "sj-ui-toolkit/src/client";
import type { LessonDetailsPublic } from "sj-ui-toolkit/src/client/svcContent";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { LESSON_TYPE_MAP } from "sj-ui-toolkit/src/atlas/utils";
import type { ContentStatus } from "sj-ui-toolkit/src/atlas/components/ContentCard/contentCardTypes";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { PageBreadcrumb } from "sj-ui-toolkit/src/atlas/components";

import { LessonsContainer } from "../Path/LessonsContainer";
import S from "../Path/PathDetails.module.scss";

const STATUS_MAP: Record<string, ContentStatus> = {
  Assigned: "notStarted",
  InProgress: "inProgress",
  Completed: "completed",
  PastDue: "notStarted",
  Stopped: "notStarted",
};

const mapChoreToLesson = (
  item: LessonChoreItem,
  lessonDetails: Record<string, LessonDetailsPublic>
) => {
  const lessonKey = item.content.lesson.configuration.lessonKey;
  const details = lessonDetails[lessonKey];

  return {
    id: String(item.assignmentTaskUuid),
    lessonKey,
    title: details?.lessonName || lessonKey,
    description: details?.lessonDescription || "",
    status: STATUS_MAP[item.content.lesson.status.progress] || "notStarted",
    contentType: (details?.lessonType && LESSON_TYPE_MAP[details.lessonType]) || "video",
  };
};

const PersonalizedLessonAssignmentPage = () => {
  const { data: choreList, isLoading } = useUserChores();
  const { selectedTenant } = useUserContext();
  const [lessonDetails, setLessonDetails] = useState<Record<string, LessonDetailsPublic>>({});

  useBreadcrumb({
    label: "Personalized Assignment",
    route: "/personalized-lesson-assignment",
    display: "page",
  });

  const pla = useMemo(() => {
    return getPersonalizedLessonAssignment(choreList?.items);
  }, [choreList]);

  useEffect(() => {
    if (!pla) return undefined;

    const lessonKeys = [...pla.notCompleted, ...pla.completed].map(
      item => item.content.lesson.configuration.lessonKey
    );
    if (!lessonKeys.length) return undefined;

    let cancelled = false;
    getContentClient()
      .getLessonsPublic(lessonKeys, selectedTenant)
      .then(lessons => {
        if (cancelled) return;
        setLessonDetails(
          Object.fromEntries(lessons.map(lesson => [lesson.lessonKey ?? "", lesson]))
        );
      })
      .catch(error => {
        console.error("Failed to fetch PLA lesson details:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [pla, selectedTenant]);

  const remainingLessons = useMemo(() => {
    return (pla?.notCompleted ?? []).map(item => mapChoreToLesson(item, lessonDetails));
  }, [pla?.notCompleted, lessonDetails]);

  const completedLessons = useMemo(() => {
    return (pla?.completed ?? []).map(item => mapChoreToLesson(item, lessonDetails));
  }, [pla?.completed, lessonDetails]);

  if (isLoading) {
    return (
      <div data-testid="learn-pla-page">
        <PageBreadcrumb />
        <p>Loading...</p>
      </div>
    );
  }

  if (!pla) {
    return (
      <div data-testid="learn-pla-page">
        <PageBreadcrumb />
        <h1 className="atlas-h2 mt-8">No Personalized Assignment</h1>
        <p className="body-lg">You don't have any personalized lessons assigned at this time.</p>
      </div>
    );
  }

  return (
    <div data-testid="learn-pla-page">
      <PageBreadcrumb />

      <div className={C(S.pathDetailsContainer, "pb-8")}>
        <Tag
          value="Personalized Assignment"
          severity="success"
          rounded={true}
          className="self-start"
        />
        <h1 className={C("atlas-h2-alt", "leading-tight")}>{pla.name}</h1>
        <p className="body-lg">{pla.description}</p>

        <div className={S.pathProgressContainer} role="region" aria-label="Assignment progress">
          <div className={S.section} role="group" aria-labelledby="stat-progress">
            <h2 className={C(S.mutedText, "atlas-h5")} id="stat-progress">
              Progress
            </h2>
            <p className="atlas-h2">{pla.progressPercent}%</p>
          </div>

          <div className={S.section} role="group" aria-labelledby="stat-lessons-completed">
            <h2 className={C(S.mutedText, "atlas-h5")} id="stat-lessons-completed">
              Lessons Completed
            </h2>
            <p className="atlas-h2">{pla.completedLessons}</p>
            <p className={S.mutedText}>/ {pla.totalLessons} total lessons</p>
          </div>
        </div>
      </div>

      {remainingLessons.length > 0 && (
        <LessonsContainer lessons={remainingLessons} title="Lessons to do" />
      )}

      {completedLessons.length > 0 && (
        <LessonsContainer lessons={completedLessons} title="Completed Lessons" />
      )}
    </div>
  );
};

export default PersonalizedLessonAssignmentPage;
